import { useState } from "react";
import { ptBR } from "date-fns/locale";
import { Calendar } from "@/ui/calendar";
import { Input } from "@/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/popover";
import { HugeiconsIcon } from "@hugeicons/react";
import { Calendar03Icon } from "@hugeicons/core-free-icons";

/**
 * O campo de data da casa: o calendário do shadcn num popover.
 *
 * Substituiu o `<input type="date">` do navegador em toda a mesa. O nativo
 * desenhava "mm/dd/yyyy" e um calendário em inglês que começa no domingo —
 * o idioma do SISTEMA, não o do escritório —, e cada navegador pinta o seu
 * (o do Chrome transbordava da coluna da ficha). Aqui é pt-BR, "dd/mm/aaaa",
 * o mesmo desenho em todo lugar.
 *
 * O valor continua sendo a string que o banco guarda: `AAAA-MM-DD`, e com
 * `comHora` o `AAAA-MM-DDTHH:MM` que o `datetime-local` entregava — quem
 * chama não muda nada além do componente.
 */
export function CampoDeData({
  valor,
  aoMudar,
  rotulo,
  comHora = false,
  dica = "escolher",
  className = "",
  id,
  autoFocus,
  limpar = true,
}: {
  valor?: string | null;
  aoMudar: (valor: string) => void;
  rotulo?: string;
  comHora?: boolean;
  dica?: string;
  className?: string;
  id?: string;
  autoFocus?: boolean;
  limpar?: boolean;
}) {
  const [aberto, setAberto] = useState(Boolean(autoFocus));
  const texto = valor ? String(valor) : "";
  const dia = paraData(texto.slice(0, 10));
  const hora = comHora ? texto.slice(11, 16) || "09:00" : "";

  const montar = (d: Date | undefined, h = hora) => {
    if (!d) return "";
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return comHora ? `${iso}T${h || "09:00"}` : iso;
  };

  const escrito = dia
    ? new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(dia) +
      (comHora ? ` ${hora}` : "")
    : "";

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger
        render={
          <button
            type="button"
            id={id}
            aria-label={rotulo ? `${rotulo}${escrito ? `, ${escrito}` : ""}` : undefined}
            className={`flex w-full cursor-pointer items-center gap-2 text-left ${className}`}
          />
        }
      >
        <span
          data-vazio={escrito ? undefined : ""}
          className={`flex-1 truncate font-mono ${escrito ? "" : "text-tinta-fraca italic font-sans"}`}
        >
          {escrito || dica}
        </span>
        {/* ponytail: a medida do `Icone` da casa (16, traço 1,8) escrita aqui,
            porque `visual.jsx` não tem tipo e este arquivo é TypeScript. */}
        <HugeiconsIcon icon={Calendar03Icon} size={16} strokeWidth={1.8} className="flex-none text-tinta-fraca" />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="z-[120] w-auto gap-0 rounded-cartao border border-border bg-card p-0 shadow-[0_14px_36px_color-mix(in_oklab,_var(--color-foreground)_12%,_transparent)]"
      >
        <Calendar
          mode="single"
          locale={ptBR}
          captionLayout="dropdown"
          startMonth={new Date(1930, 0)}
          endMonth={new Date(new Date().getFullYear() + 10, 11)}
          defaultMonth={dia ?? undefined}
          selected={dia ?? undefined}
          onSelect={(d) => {
            aoMudar(montar(d));
            if (!comHora) setAberto(false);
          }}
        />
        {comHora || (limpar && texto) ? (
          <div className="flex items-center gap-2 border-t border-border p-2">
            {comHora ? (
              <Input
                type="time"
                aria-label="Hora"
                value={hora}
                onChange={(e) => dia && aoMudar(montar(dia, e.target.value))}
                className="h-mini w-auto rounded-controle font-mono text-corpo"
              />
            ) : null}
            <span className="flex-1" />
            {limpar && texto ? (
              <button
                type="button"
                onClick={() => {
                  aoMudar("");
                  setAberto(false);
                }}
                className="h-mini cursor-pointer rounded-controle px-2.5 text-apoio font-semibold text-tinta-media transition-colors hover:bg-foreground/8 hover:text-foreground"
              >
                Limpar
              </button>
            ) : null}
            {comHora ? (
              <button
                type="button"
                onClick={() => setAberto(false)}
                /* Primário da casa: tinta, e o ponteiro escurece um degrau. Sem hover o
                   botão parecia rótulo. */
                className="h-mini cursor-pointer rounded-controle bg-tinta px-3 text-apoio font-semibold text-mesa transition-colors hover:bg-tinta/86"
              >
                Pronto
              </button>
            ) : null}
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/* `new Date("2026-09-24")` é meia-noite em UTC, que no Brasil é o dia 23 às 21h:
   o calendário marcaria a véspera. A data é montada no fuso de quem olha. */
function paraData(iso: string) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}
